import { useEffect, useState } from 'react';
import type { FormEvent } from 'react';
import {
  Button,
  Card,
  CardDescription,
  CardHeader,
  CardTitle,
  Drawer,
  EmptyState,
  Field,
  FormAlert,
  Input,
  Skeleton,
  TrafficLight,
  useToast,
} from '@mgdlead/ui';
import { can } from '@mgdlead/shared';
import { Target } from 'lucide-react';

import { useWorkspace } from '../workspace/WorkspaceContext';
import {
  useGoalsOverview,
  useSetGoal,
  type CampaignGoalStatus,
  type GoalIndicator,
} from '../../lib/api';
import { domainMessage, humanizeError } from '../../lib/errors';
import {
  apenasDigitos,
  bpDePercentual,
  centavosDoCampo,
  digitosDeCentavos,
  digitosDeMilicentavos,
  mascaraDeCentavos,
  mascaraDeLucro,
  milicentavosDoCampo,
  percentualDeBp,
} from './goals-ui';

/**
 * G3 — Configurações → Metas. Um cartão por bloco com o número de agora, a meta e o
 * semáforo — tudo pronto do servidor. "Definir meta" abre o formulário num Drawer.
 * Sem meta: só o número, sem semáforo (spec G3).
 */
export function GoalsSection() {
  const { workspace, role } = useWorkspace();
  const overview = useGoalsOverview(workspace.id);
  const editable = can(role, 'campaign:update');
  const [editando, setEditando] = useState<CampaignGoalStatus | null>(null);

  if (overview.isPending) {
    return (
      <div className="flex flex-col gap-3" aria-busy="true">
        <Skeleton className="h-6 w-56" />
        <Skeleton className="h-40 w-full rounded-lg" />
        <Skeleton className="h-40 w-full rounded-lg" />
      </div>
    );
  }
  if (overview.isError || !overview.data) {
    return <FormAlert>{humanizeError(overview.error)}</FormAlert>;
  }
  const { campanhas, mes } = overview.data;

  return (
    <div className="flex flex-col gap-4">
      <Card tone="sunken">
        <CardHeader>
          <CardTitle>Metas do mês por campanha</CardTitle>
          <CardDescription>
            Verde: dentro da meta. Amarelo: até 10% pior que a meta. Vermelho: mais que isso. A meta
            vale para o mês e continua nos meses seguintes até você mudar.
          </CardDescription>
        </CardHeader>
      </Card>

      {campanhas.length === 0 ? (
        <EmptyState
          kind="no-data"
          icon={Target}
          title="Nenhuma campanha ainda"
          description="Crie uma campanha para definir metas de custo, evasão e lucro."
        />
      ) : (
        <div className="grid gap-3 md:grid-cols-2">
          {campanhas.map((c) => (
            <CampanhaMetas
              key={c.campaignId}
              campanha={c}
              editable={editable}
              onEditar={() => setEditando(c)}
            />
          ))}
        </div>
      )}

      <GoalDrawer campanha={editando} mes={mes} onClose={() => setEditando(null)} />
    </div>
  );
}

function CampanhaMetas({
  campanha,
  editable,
  onEditar,
}: {
  campanha: CampaignGoalStatus;
  editable: boolean;
  onEditar: () => void;
}) {
  const semMeta = campanha.meta === null;
  return (
    <Card className="flex flex-col gap-3">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="truncate font-medium text-foreground">{campanha.nome}</p>
          <p className="text-xs text-muted-foreground">
            {semMeta
              ? 'Sem meta definida'
              : `Meta vigente desde ${mesPorExtenso(campanha.meta!.vigenteDesde)}`}
          </p>
        </div>
        <TrafficLight status={campanha.pior} />
      </div>

      <dl className="flex flex-col divide-y divide-border text-sm">
        {campanha.indicadores.map((i) => (
          <IndicadorLinha key={i.chave} indicador={i} />
        ))}
      </dl>

      {editable ? (
        <div className="border-t border-border pt-3">
          <Button variant={semMeta ? 'secondary' : 'ghost'} size="sm" onClick={onEditar}>
            {semMeta ? 'Definir meta' : 'Editar metas'}
          </Button>
        </div>
      ) : null}
    </Card>
  );
}

function IndicadorLinha({ indicador: i }: { indicador: GoalIndicator }) {
  const detalhe = i.motivo ?? i.base;
  return (
    <div className="flex items-start justify-between gap-3 py-2">
      <div className="min-w-0">
        <dt className="text-foreground">{i.rotulo}</dt>
        <dd className="text-xs text-muted-foreground">
          {i.metaTexto ? `Meta: ${i.metaTexto}` : 'Sem meta'}
          {detalhe ? ` · ${detalhe}` : null}
        </dd>
      </div>
      <dd className="flex shrink-0 items-center gap-2">
        <span className="nums font-medium text-foreground">{i.valorTexto}</span>
        <TrafficLight status={i.semaforo} hint={i.dica ?? undefined} compact />
      </dd>
    </div>
  );
}

function mesPorExtenso(mes: string): string {
  const [ano, m] = mes.split('-');
  const nomes = [
    'janeiro',
    'fevereiro',
    'março',
    'abril',
    'maio',
    'junho',
    'julho',
    'agosto',
    'setembro',
    'outubro',
    'novembro',
    'dezembro',
  ];
  return `${nomes[Number(m) - 1] ?? m} de ${ano}`;
}

/* ========================================================================== */
/*  O formulário                                                               */
/* ========================================================================== */

type Campos = {
  cpl: string;
  d1: string;
  d7: string;
  investimento: string;
  lucro: string;
};

function camposDe(c: CampaignGoalStatus | null): Campos {
  const m = c?.meta ?? null;
  return {
    cpl: digitosDeCentavos(m?.cplMaxCents ?? null),
    d1: percentualDeBp(m?.evasionD1MaxBp ?? null),
    d7: percentualDeBp(m?.evasionD7MaxBp ?? null),
    investimento: digitosDeCentavos(m?.dailySpendMaxCents ?? null),
    lucro: digitosDeMilicentavos(m?.leadProfitMinMillicents ?? null),
  };
}

function GoalDrawer({
  campanha,
  mes,
  onClose,
}: {
  campanha: CampaignGoalStatus | null;
  /** `YYYY-MM-01` do mês corrente: "limpar" só apaga a meta gravada NESTE mês. */
  mes: string;
  onClose: () => void;
}) {
  const { workspace } = useWorkspace();
  const setGoal = useSetGoal();
  const { toast } = useToast();
  const [campos, setCampos] = useState<Campos>(() => camposDe(campanha));
  const [erro, setErro] = useState<string | null>(null);

  useEffect(() => {
    setCampos(camposDe(campanha));
    setErro(null);
  }, [campanha]);

  const d1 = bpDePercentual(campos.d1);
  const d7 = bpDePercentual(campos.d7);
  const pctInvalido = (v: number | null) => v !== null && (Number.isNaN(v) || v > 10_000);

  const enviar = (metas: {
    cplMaxCents: number | null;
    evasionD1MaxBp: number | null;
    evasionD7MaxBp: number | null;
    dailySpendMaxCents: number | null;
    leadProfitMinMillicents: number | null;
  }) => {
    if (!campanha) return;
    setErro(null);
    setGoal.mutate(
      { workspaceId: workspace.id, campaignId: campanha.campaignId, metas },
      {
        onSuccess: () => {
          toast({ title: 'Metas salvas', description: campanha.nome, tone: 'success' });
          onClose();
        },
        onError: (e) => setErro(domainMessage(e) ?? humanizeError(e)),
      },
    );
  };

  const salvar = (event: FormEvent) => {
    event.preventDefault();
    if (pctInvalido(d1) || pctInvalido(d7)) {
      setErro('Evasão é um percentual entre 0 e 100, com até 2 casas.');
      return;
    }
    enviar({
      cplMaxCents: centavosDoCampo(campos.cpl),
      evasionD1MaxBp: d1,
      evasionD7MaxBp: d7,
      dailySpendMaxCents: centavosDoCampo(campos.investimento),
      leadProfitMinMillicents: milicentavosDoCampo(campos.lucro),
    });
  };

  const dinheiro = (campo: 'cpl' | 'investimento') => ({
    inputMode: 'numeric' as const,
    value: mascaraDeCentavos(campos[campo]),
    placeholder: '0,00',
    onChange: (e: { target: { value: string } }) =>
      setCampos((c) => ({ ...c, [campo]: apenasDigitos(e.target.value) })),
  });
  const pct = (campo: 'd1' | 'd7') => ({
    inputMode: 'decimal' as const,
    value: campos[campo],
    placeholder: 'ex.: 20',
    onChange: (e: { target: { value: string } }) =>
      setCampos((c) => ({ ...c, [campo]: e.target.value.slice(0, 6) })),
  });

  return (
    <Drawer
      open={campanha !== null}
      onOpenChange={(open) => (open ? null : onClose())}
      title={campanha ? `Metas de ${campanha.nome}` : 'Metas'}
      description="Deixe em branco o que não quiser acompanhar: sem meta, o número aparece sem semáforo."
    >
      <form className="flex flex-col gap-4" onSubmit={salvar} noValidate>
        {erro ? <FormAlert>{erro}</FormAlert> : null}
        <Field label="Meta de CPL real: até (R$)" hint="Gasto dividido pelas entradas no mês.">
          {(control) => <Input {...control} {...dinheiro('cpl')} />}
        </Field>
        <Field label="Meta de evasão no 1º dia: até (%)" hint="Quem saiu por conta própria.">
          {(control) => <Input {...control} {...pct('d1')} />}
        </Field>
        <Field label="Meta de evasão em 7 dias: até (%)">
          {(control) => <Input {...control} {...pct('d7')} />}
        </Field>
        <Field label="Investimento diário: até (R$)" hint="Média dos dias fechados do mês.">
          {(control) => <Input {...control} {...dinheiro('investimento')} />}
        </Field>
        <Field label="Lucro por lead: no mínimo (R$)" hint="Com 4 casas, ex.: 0,0500.">
          {(control) => (
            <Input
              {...control}
              inputMode="numeric"
              placeholder="0,0000"
              value={mascaraDeLucro(campos.lucro)}
              onChange={(e) =>
                setCampos((c) => ({ ...c, lucro: apenasDigitos(e.target.value).slice(0, 7) }))
              }
            />
          )}
        </Field>
        <div className="flex flex-col gap-2 md:flex-row">
          <Button
            type="submit"
            variant="primary"
            block
            className="md:w-auto"
            loading={setGoal.isPending}
            loadingLabel="Salvando"
          >
            Salvar metas
          </Button>
          {/*
            Limpar apaga a linha do mês corrente; a meta herdada de um mês anterior continua
            valendo (o servidor guarda só o que a pessoa escreveu). Por isso o botão só aparece
            quando a meta vigente foi gravada neste mês.
          */}
          {campanha?.meta?.vigenteDesde === mes ? (
            <Button
              type="button"
              variant="neutral"
              block
              className="md:w-auto"
              disabled={setGoal.isPending}
              onClick={() =>
                enviar({
                  cplMaxCents: null,
                  evasionD1MaxBp: null,
                  evasionD7MaxBp: null,
                  dailySpendMaxCents: null,
                  leadProfitMinMillicents: null,
                })
              }
            >
              Limpar metas do mês
            </Button>
          ) : null}
        </div>
      </form>
    </Drawer>
  );
}
